import { defer, firstValueFrom, switchMap, tap, timeout } from 'rxjs';

import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, inject } from '@angular/core';

import { SessionTokenStore, UserStore } from '@cxc/core/services';
import { IdentityAuthAPI } from '@services/API/identity/auth.service';
import { UsersAPI } from '@services/API/identity/users.service';

import { SessionChannel } from './session-channel.service';
import { SessionRefreshService } from './session-refresh.service';
import { sessionRejected } from './session-rejected';

/**
 * The whole restore (refresh, then profile) runs in an APP_INITIALIZER behind
 * the boot loader, and `UsersAPI.get` retries forever: one budget bounds it.
 */
export const RESTORE_TIMEOUT_MS = 15_000;

/**
 * What a failed `restore()` reports about an HTTP error: its status and url,
 * never the response itself.
 *
 * `Sentry.createErrorHandler()` does not ship an `HttpErrorResponse` whole --
 * it reduces it first -- but what survives depends on the shape of a body we
 * do not control: a string body is embedded verbatim in the message
 * (`Server returned code N with body "..."`), and an Error-like body is
 * captured as the error itself. Only a plain JSON body falls back to the
 * generic message. With `sendDefaultPii: true` and a `beforeSend` that only
 * filters by environment (`main.ts`), nothing downstream narrows it again.
 *
 * The status and the url are the whole diagnosis here, so reporting them
 * directly makes the event the same whichever shape the body takes
 * (MR !1318 review, gatekeeper 4).
 */
function reportable(error: unknown): unknown {
  return error instanceof HttpErrorResponse
    ? new Error(`Session restore failed: HTTP ${error.status} on ${error.url}`)
    : error;
}

/**
 * Starts and ends the session of this tab (LEN-2764). The session no longer
 * lives in localStorage: the access token and the profile are in memory, and
 * the refresh token in an HttpOnly cookie only identity-api reads.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private refresher = inject(SessionRefreshService);
  private usersApi = inject(UsersAPI);
  private authApi = inject(IdentityAuthAPI);
  private userStore = inject(UserStore);
  private tokens = inject(SessionTokenStore);
  private channel = inject(SessionChannel);
  private errorHandler = inject(ErrorHandler);

  private loggingOut: Promise<void> | null = null;

  /**
   * Rebuilds the session after a reload or in a new tab: a new access token
   * from the refresh cookie, then the profile. Runs before the guards. With
   * no valid cookie the user simply stays logged out.
   */
  async restore(): Promise<void> {
    // The timeout below stops waiting, but cannot cancel the refresh: it may
    // still resolve after the catch cleared the session (another tab holding
    // the Web Lock). Aborting tells SessionRefreshService to drop that token.
    const abandon = new AbortController();
    // Which step the error came from: `sessionRejected` only speaks for the
    // refresh. Once a token is in hand the cookie was accepted, so a 401/403
    // from the profile is an authorization anomaly, not a session ending
    // (LEN-2945).
    let refreshed = false;
    try {
      // One timeout over both steps: a separate one per step would add up
      // (the refresh has its own) and keep the loader up to twice as long.
      const profile = await firstValueFrom(
        defer(() => this.refresher.refresh(abandon.signal)).pipe(
          // Its own operator rather than a side effect inside the `switchMap`
          // projection: that would tie the flag to the shape of this pipe, and
          // a `retry()` or a reordering later would leave it true after a
          // refresh that failed (MR !1318 review S2).
          tap(() => (refreshed = true)),
          switchMap(() => this.usersApi.get(false)),
          timeout({ first: RESTORE_TIMEOUT_MS })
        )
      );
      this.userStore.patch({ ...profile, logged: true });
    } catch (error) {
      abandon.abort();
      // Aborting only drops the token once every caller of the shared refresh
      // aborted, and the interceptor joins it without a signal. Without this
      // the refresh would store its token right after the clear below, leaving
      // one in memory with the user logged out.
      this.refresher.discardInFlightToken();
      this.tokens.clear();
      // No cookie (401/403) is the expected way a session ends. Anything else
      // (identity-api down, a timeout, a profile that will not load) also
      // leaves the user out, so it goes to Sentry through the app's
      // ErrorHandler instead of vanishing.
      //
      // Past the refresh that includes 401, not just 403: a 401 from
      // `/users/me` never reaches here raw, because the interceptor catches it,
      // refreshes and retries once (`http-authorization.interceptor.ts`).
      // Arriving here means the retry failed with a fresh token too, which is
      // the same anomaly as a 403 and not a token revoked mid-restore (MR
      // !1318 review S3).
      if (refreshed || !sessionRejected(error)) {
        this.errorHandler.handleError(reportable(error));
      }
    }
  }

  /**
   * Ends the session everywhere: identity-api revokes it in Cognito and
   * deletes the cookie, the other tabs are told, and this tab is cleared.
   * Every logout path goes through here; concurrent calls share one logout.
   */
  logout(loaderOptIn: boolean | string = false): Promise<void> {
    if (!this.loggingOut) {
      this.loggingOut = this.endSession(loaderOptIn).finally(
        () => (this.loggingOut = null)
      );
    }
    return this.loggingOut;
  }

  private async endSession(loaderOptIn: boolean | string): Promise<void> {
    // Sent before clearing: the request takes the token still in memory.
    const revoke = firstValueFrom(this.authApi.logout(loaderOptIn)).catch(
      () => undefined
    );
    this.channel.post({ type: 'LOGOUT' });
    this.userStore.logOut();
    // An expired token cannot revoke; identity-api still deletes the cookie.
    await revoke;
  }
}
